import React, { useEffect, useState } from 'react';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import WeeklyShipService from './WeeklyShipService';
import AirItemTypeService from '../setting/airItemType/AirItemTypeService';
import AirItemTypeInputs from '../../sharedComponents/AirItemTypeInputs';
import AirItemTypeTripleDropdown from '../../sharedComponents/AirItemTypeTripleDropdown';
import { toast } from 'react-toastify';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const WeeklyShipAirDetail = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [boxes, setBoxes] = useState([]);
  const [airItemTypes, setAirItemTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({}); // { weeklyShipId: true }

  useEffect(() => {
    const load = async () => {
      try {
        const res = await AirItemTypeService.getAirItemTypes({ isActive: 'true' });
        if (res?.data?.data) setAirItemTypes(res.data.data);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const handleShipDateChange = (e) => {
    const id = e.target.value;
    setShipDateId(id);
    if (id === '' || id === 'all' || id === 'none') {
      setBoxes([]);
      return;
    }
    setLoading(true);
    WeeklyShipService.getWeeklyShipAirDetailByShipDateId(parseInt(id, 10))
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setBoxes(sortBoxesSavedToBottom(list));
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to load air detail');
        setBoxes([]);
      })
      .finally(() => setLoading(false));
  };

  const getBoxActualCostItems = (b) => b.actualCostItems || [];

  const handleAddCostType = (weeklyShipId, airItemTypeId) => {
    if (!airItemTypeId) return;
    setBoxes((prev) =>
      prev.map((b) => {
        if (b.weeklyShipId !== weeklyShipId) return b;
        const existing = getBoxActualCostItems(b);
        if (existing.some((e) => e.airItemTypeId === airItemTypeId)) return b;
        const airItemType = airItemTypes.find((t) => t.id === airItemTypeId);
        return { ...b, actualCostItems: [...existing, { airItemTypeId, airItemType, weightKg: null, qty: 1 }] };
      })
    );
  };

  const handleRemoveCostType = (weeklyShipId, airItemTypeId) => {
    setBoxes((prev) =>
      prev.map((b) => {
        if (b.weeklyShipId !== weeklyShipId) return b;
        const items = getBoxActualCostItems(b).filter((e) => e.airItemTypeId !== airItemTypeId);
        return { ...b, actualCostItems: items };
      })
    );
  };

  const handleCostWeightChange = (weeklyShipId, itemIdOrTypeId, value) => {
    setBoxes((prev) =>
      prev.map((b) => {
        if (b.weeklyShipId !== weeklyShipId) return b;
        const items = getBoxActualCostItems(b).map((it) =>
          (it.id ?? it.airItemTypeId) === itemIdOrTypeId ? { ...it, weightKg: value === '' ? null : value } : it
        );
        return { ...b, actualCostItems: items };
      })
    );
  };

  const handleCostQtyChange = (weeklyShipId, itemIdOrTypeId, value) => {
    setBoxes((prev) =>
      prev.map((b) => {
        if (b.weeklyShipId !== weeklyShipId) return b;
        const items = getBoxActualCostItems(b).map((it) =>
          (it.id ?? it.airItemTypeId) === itemIdOrTypeId ? { ...it, qty: value === '' ? 1 : parseInt(value, 10) || 1 } : it
        );
        return { ...b, actualCostItems: items };
      })
    );
  };

  const handleSaveCostItems = async (weeklyShipId) => {
    const b = boxes.find((x) => x.weeklyShipId === weeklyShipId);
    if (!b) return;
    const costItems = getBoxActualCostItems(b);
    const isSinglePerKg = costItems.length === 1 && costItems[0].airItemType?.calculationMethod === 'per_kg';
    const boxWeight = b.weight != null && b.weight !== '' ? parseFloat(b.weight) : null;
    const items = costItems.map((it) => {
      let weightKg = it.weightKg != null && it.weightKg !== '' ? parseFloat(it.weightKg) : null;
      if (it.airItemType?.calculationMethod === 'per_kg' && weightKg == null && isSinglePerKg && boxWeight != null) {
        weightKg = boxWeight;
      }
      return {
        airItemTypeId: it.airItemTypeId,
        weightKg,
        qty: it.qty != null && it.qty !== '' ? parseInt(it.qty, 10) : 1,
      };
    });
    setSaving((prev) => ({ ...prev, [weeklyShipId]: true }));
    try {
      const res = await WeeklyShipService.setWeeklyShipActualCostItems(weeklyShipId, items);
      const updatedItems = res?.data?.actualCostItems || [];
      setBoxes((prev) => {
        const updated = prev.map((x) =>
          x.weeklyShipId === weeklyShipId ? { ...x, actualCostItems: updatedItems } : x
        );
        return sortBoxesSavedToBottom(updated);
      });
      toast.success('บันทึกต้นทุนกล่องแล้ว');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving((prev) => ({ ...prev, [weeklyShipId]: false }));
    }
  };

  const isBoxCostSaved = (b) => {
    const items = getBoxActualCostItems(b);
    return items.length > 0 && items.every((it) => it.id != null);
  };

  const sortByBoxNoAsc = (a, b) =>
    (a.boxNo != null && b.boxNo != null ? String(a.boxNo).localeCompare(String(b.boxNo), undefined, { numeric: true }) : 0);

  const sortBoxesSavedToBottom = (list) => {
    const unsaved = list.filter((b) => !isBoxCostSaved(b)).sort(sortByBoxNoAsc);
    const saved = list.filter((b) => isBoxCostSaved(b)).sort(sortByBoxNoAsc);
    return [...unsaved, ...saved];
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        พี่โซ่ ใส่ราคาต้นทุน
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ShipDateDropdown
          airOnly={true}
          value={shipDateId}
          handleDataChange={handleShipDateChange}
          includeDefault={true}
        />
        {loading && <CircularProgress size={24} />}
      </Box>

      {!shipDateId || shipDateId === 'all' || shipDateId === 'none' ? (
        <Typography color="text.secondary">เลือกรอบเรือ (เครื่องบิน) เพื่อดูรายละเอียด</Typography>
      ) : (
        <Box>
          {boxes.map((b) => {
            const costItems = getBoxActualCostItems(b);
            const isSinglePerKg = costItems.length === 1 && costItems[0].airItemType?.calculationMethod === 'per_kg';
            const saved = isBoxCostSaved(b);
            return (
              <Paper
                key={b.weeklyShipId}
                sx={{
                  mb: 3,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography fontWeight="bold" sx={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    กล่องที่ {b.boxNo} (น้ำหนัก: {b.weight != null ? `${b.weight} kg` : '-'})
                    {saved && <CheckCircleOutlineIcon fontSize="small" color="success" titleAccess="บันทึกแล้ว" />}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', maxWidth: '100%' }}>
                    <AirItemTypeTripleDropdown
                      list={airItemTypes}
                      value=""
                      onChange={(id) => handleAddCostType(b.weeklyShipId, id)}
                      placeholder="เพิ่มชนิดต้นทุน — เลือก category → type → ชื่อ"
                      disabled={!!saving[b.weeklyShipId]}
                      label="เพิ่มชนิดต้นทุน"
                      className="w-full min-w-0 sm:min-w-[240px] max-w-full"
                    />
                  </Box>
                  {costItems.length > 0 && (
                    <Box sx={{ flex: '1 1 100%', mt: 0.5 }}>
                      {costItems.map((it) => {
                        const type = it.airItemType || airItemTypes.find((t) => t.id === it.airItemTypeId);
                        let costLabel = null;
                        if (type) {
                          if (type.costRules && type.costCalculationMethod === 'per_kg') {
                            const r = type.costRules;
                            costLabel = r.firstStepAmount != null
                              ? `ต้นทุนตามขั้น: แรก ${r.firstKg ?? '?'} kg = ฿${Number(r.firstStepAmount).toLocaleString()}, เกิน ฿${Number(r.amountPerKgOver ?? 0).toLocaleString()}/kg`
                              : `ต้นทุนตามขั้น: แรก ฿${Number(r.amountPerKgFirst ?? 0).toLocaleString()}/kg, เกิน ฿${Number(r.amountPerKgOver ?? 0).toLocaleString()}/kg`;
                          } else {
                            costLabel = type.costCalculationMethod === 'per_kg'
                              ? `ต้นทุน ฿${Number(type.costAmountPerUnit || 0).toLocaleString()} ต่อ kg`
                              : `ต้นทุน ฿${Number(type.costAmountPerUnit || 0).toLocaleString()} ต่อชิ้น`;
                          }
                        }
                        return (
                        <Box key={it.airItemTypeId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
                            {type?.typeName || it.airItemTypeId}
                          </Typography>
                          {costLabel && (
                            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              ({costLabel})
                            </Typography>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveCostType(b.weeklyShipId, it.airItemTypeId)}
                            disabled={!!saving[b.weeklyShipId]}
                            aria-label="ลบชนิด"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        );
                      })}
                      <AirItemTypeInputs
                        items={costItems.map((it) => ({ ...it, airItemType: it.airItemType || airItemTypes.find((t) => t.id === it.airItemTypeId) }))}
                        onWeightChange={(id, value) => handleCostWeightChange(b.weeklyShipId, id, value)}
                        onQtyChange={(id, value) => handleCostQtyChange(b.weeklyShipId, id, value)}
                        disabled={!!saving[b.weeklyShipId]}
                        singlePerKgBoxWeight={isSinglePerKg ? b.weight : null}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveCostItems(b.weeklyShipId)}
                        disabled={!!saving[b.weeklyShipId]}
                        sx={{ mt: 1 }}
                      >
                        {saving[b.weeklyShipId] ? 'กำลังบันทึก...' : 'บันทึกต้นทุนกล่อง'}
                      </Button>
                    </Box>
                  )}
                  {saving[b.weeklyShipId] && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
                  <Box sx={{ flex: '1 1 100%', mt: 0.5 }}>
                    {(b.orders || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">ไม่มีออเดอร์ในกล่องนี้</Typography>
                    ) : (
                      (b.orders || []).map((o) => (
                        <Box key={o.orderId} sx={{ mb: 0.5 }}>
                          <a href={o.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                            {o.url || '-'}
                          </a>
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
          {boxes.length === 0 && !loading && (
            <Typography color="text.secondary">ไม่มีกล่องสำหรับรอบเรือนี้</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WeeklyShipAirDetail;
